/**
 * ExpressionPreview — read-only formula text + parse status (sliders mode).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { expandSubrefsForDisplay } from '@trafficpulse/shared';
import type { FormulaSubformula } from '@trafficpulse/shared';

import {
  evaluateExpressionState,
  expressionStatusLabel,
} from './expression-preview.state';

export interface ExpressionPreviewProps {
  expression: string;
  /** Expands internal `__sub_<id>` tokens using live subformula expressions. */
  expandSubrefsWith?: FormulaSubformula[];
}

const ExpressionPreview: FC<ExpressionPreviewProps> = ({
  expression,
  expandSubrefsWith,
}) => {
  const state = useMemo(() => evaluateExpressionState(expression), [expression]);
  const displayText = useMemo(() => {
    if (!expandSubrefsWith?.length) return expression || '(no expression)';
    return expandSubrefsForDisplay(expression, expandSubrefsWith) || '(no expression)';
  }, [expression, expandSubrefsWith]);
  const canonical = (expression || '').trim();
  const expanded = displayText.trim();
  const showExpandedHint =
    Boolean(expandSubrefsWith?.length) &&
    Boolean(canonical) &&
    expanded !== canonical;

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Expression</p>
        <span className={state.isValid ? 'text-xs text-success' : 'text-xs text-destructive'}>
          {expressionStatusLabel(state)}
        </span>
      </div>
      {showExpandedHint ? (
        <p className="mt-1 text-[10px] text-muted-foreground">
          Full calculation (subformulas expanded). Saved config still uses internal references for
          scoring.
        </p>
      ) : null}
      <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">{displayText}</pre>
      {state.error ? (
        <p className="mt-1 text-[11px] text-destructive">{state.error}</p>
      ) : null}
    </div>
  );
};

export default ExpressionPreview;
